import { motion } from 'framer-motion';
import { 
  Workflow,
  ArrowRightCircle,
  FileCheck,
  Users,
  BarChart,
  Building2,
  Mail,
  UserCheck,
  FileText,
  UserPlus,
  MoreHorizontal,
  Zap
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';

const automationBenefits = [
  {
    icon: <Zap className="w-6 h-6" />,
    title: "Instant Execution",
    description: "Automate tasks to run instantly when triggered"
  },
  {
    icon: <FileCheck className="w-6 h-6" />,
    title: "Zero Errors",
    description: "Eliminate human errors in repetitive workflows"
  },
  {
    icon: <Users className="w-6 h-6" />,
    title: "Team Efficiency",
    description: "Let your team focus on strategic work"
  },
  {
    icon: <BarChart className="w-6 h-6" />,
    title: "Full Analytics",
    description: "Track and optimize workflow performance"
  }
];

const workflowSteps = [
  { icon: <FileText className="w-5 h-5" />, text: "Document received" },
  { icon: <FileCheck className="w-5 h-5" />, text: "Data extracted" },
  { icon: <Mail className="w-5 h-5" />, text: "Notifications sent" },
  { icon: <UserCheck className="w-5 h-5" />, text: "Lead qualified" },
  { icon: <Building2 className="w-5 h-5" />, text: "CRM updated" },
  { icon: <FileCheck className="w-5 h-5" />, text: "Process completed" }
];

const WorkflowAnimation = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const { theme } = useTheme();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isMobile) {
      const interval = setInterval(() => {
        setCurrentStep((prev) => (prev + 1) % workflowSteps.length);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [isMobile]);

  if (isMobile) {
    return (
      <div className={`relative h-48 overflow-hidden rounded-xl ${
        theme === 'dark' ? 'bg-gray-800/50' : 'bg-gray-100'
      } p-4`}>
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center"
          >
            <div className={`${
              theme === 'dark' ? 'bg-gray-700' : 'bg-white'
            } p-4 rounded-lg text-blue-400 mb-4`}>
              {workflowSteps[currentStep].icon}
            </div>
            <span className={`text-sm ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            } text-center`}>
              {workflowSteps[currentStep].text}
            </span>
            <div className="mt-4 flex space-x-2">
              {workflowSteps.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full ${
                    index === currentStep ? 'bg-blue-500' : theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative h-48 overflow-hidden rounded-xl ${
      theme === 'dark' ? 'bg-gray-800/50' : 'bg-gray-100'
    } p-4`}>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="flex items-center px-8">
          {workflowSteps.map((step, index) => (
            <motion.div
              key={index}
              className="flex items-center"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.5, duration: 0.5 }}
            >
              <motion.div
                className="flex flex-col items-center mx-6"
                whileHover={{ scale: 1.1 }}
              >
                <div className={`${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-white shadow-md'
                } p-3 rounded-lg text-blue-400`}>
                  {step.icon}
                </div>
                <span className={`text-xs ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                } mt-2 text-center whitespace-nowrap`}>
                  {step.text}
                </span>
              </motion.div>
              {index < workflowSteps.length - 1 && (
                <motion.div
                  animate={{
                    x: [-4, 4, -4],
                    opacity: [0.3, 1, 0.3]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: index * 0.5
                  }}
                >
                  <ArrowRightCircle className="w-4 h-4 text-blue-500/50" />
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

const automationTasks = [
  { icon: <Building2 className="w-5 h-5" />, text: "CRM Integration" },
  { icon: <Mail className="w-5 h-5" />, text: "Lead Generation + Outreach" },
  { icon: <UserCheck className="w-5 h-5" />, text: "Lead Qualification" },
  { icon: <UserPlus className="w-5 h-5" />, text: "Employee Onboarding" },
  { icon: <FileText className="w-5 h-5" />, text: "Invoice Processing" },
  { icon: <MoreHorizontal className="w-5 h-5" />, text: "And much more..." }
];

export default function ProcessAutomationSection() {
  const { theme } = useTheme();
  
  return (
    <section id="process-automation-section" className={`py-20 px-4 ${
      theme === 'dark' ? 'bg-black' : 'bg-gray-50'
    }`}>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Workflow className="w-12 h-12 text-blue-500" />
          <h2 className={`text-3xl font-bold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>Workflow Automation</h2>
        </div>

        <div className="prose prose-invert max-w-none mb-12">
          <p className={`text-lg ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Transform manual, time-consuming tasks into streamlined, efficient workflows. Our solutions eliminate repetitive work, reduce errors, and free up your team to focus on strategic initiatives.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {automationBenefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`${
                theme === 'dark' ? 'bg-gray-900' : 'bg-white shadow-lg'
              } p-6 rounded-xl`}
            >
              <div className="text-blue-500 mb-4">{benefit.icon}</div>
              <h3 className={`text-xl font-semibold mb-2 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>{benefit.title}</h3>
              <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </div>

        <div className="mb-12">
          <WorkflowAnimation />
        </div>

        <div className={`${
          theme === 'dark' ? 'bg-gray-900' : 'bg-white shadow-lg'
        } p-8 rounded-xl`}>
          <h3 className={`text-2xl font-bold mb-6 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>Capable of automating workflows such as:</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {automationTasks.map((task, index) => (
              <div key={index} className={`flex items-center gap-3 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                <div className="text-blue-500">{task.icon}</div>
                <span>{task.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}